import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import Fuse from 'fuse.js';
import { X } from 'lucide-react';
import { Virtuoso } from 'react-virtuoso';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LazyImage from './LazyImage';

export interface MultiSelectOption {
  id: string;
  label: string;
  icon?: string | null;
  sublabel?: string;
}

interface MultiSelectComboBoxProps {
  label: string;
  placeholder?: string;
  searchPlaceholder?: string;
  selectedItems: MultiSelectOption[];
  items: MultiSelectOption[];
  onSelectionChange: (selected: MultiSelectOption[]) => void;
  isDisabled?: boolean;
  fuseKeys?: string[];
  maxHeight?: number;
  width?: number | string;
  debounceMs?: number;
  maxDisplayItems?: number;
  single?: boolean;
  hideLabel?: boolean;
}

const DEBOUNCE_MS = 200;

export default function MultiSelectComboBox({
  label,
  placeholder = 'Select items',
  searchPlaceholder = 'Search...',
  selectedItems,
  items,
  onSelectionChange,
  isDisabled = false,
  fuseKeys = ['label'],
  width = '100%',
  debounceMs = DEBOUNCE_MS,
  maxHeight = 240,
  single = false,
  hideLabel = false,
}: MultiSelectComboBoxProps) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedInput(inputValue);
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [inputValue, debounceMs]);

  const fuseInstance = useMemo(() => {
    return new Fuse(items, { keys: fuseKeys, threshold: 0.3 });
  }, [items, fuseKeys]);

  const filteredItems = useMemo(() => {
    const selectedIds = new Set(selectedItems.map((s) => s.id));

    let availableItems: MultiSelectOption[];

    if (debouncedInput === '') {
      availableItems =
        single ? items : items.filter((item) => !selectedIds.has(item.id));
    } else {
      availableItems = fuseInstance
        .search(debouncedInput)
        .map(({ item }) => item)
        .filter((item) => !selectedIds.has(item.id));
    }

    return availableItems;
  }, [debouncedInput, items, selectedItems, fuseInstance, single]);

  const handleRemove = useCallback(
    (id: string) => {
      const newSelection = selectedItems.filter((item) => item.id !== id);
      onSelectionChange(newSelection);
    },
    [selectedItems, onSelectionChange],
  );

  const handleAdd = useCallback(
    (item: MultiSelectOption) => {
      if (!selectedItems.find((s) => s.id === item.id)) {
        onSelectionChange(single ? [item] : [...selectedItems, item]);
        setInputValue('');
        setDebouncedInput('');
        setIsOpen(false);
      }
    },
    [selectedItems, onSelectionChange, single],
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: globalThis.PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as globalThis.Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="flex flex-col gap-2" style={{ width }}>
      {!single && selectedItems.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm">{`Selected ${label}`}</Label>
          <div className="flex flex-wrap gap-2">
            {selectedItems.map((item) => (
              <div
                key={item.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary text-secondary-foreground rounded-full text-sm"
              >
                {item.icon && (
                  <LazyImage
                    src={item.icon}
                    alt=""
                    className="w-4 h-4 rounded-sm"
                    width={16}
                    height={16}
                  />
                )}
                <span>{item.label}</span>
                {item.sublabel && (
                  <span className="text-xs text-muted-foreground">
                    {item.sublabel}
                  </span>
                )}
                <button
                  onClick={() => handleRemove(item.id)}
                  className="ml-1 hover:text-destructive focus:outline-none"
                  disabled={isDisabled}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        {!hideLabel && (
          <Label className="text-sm mb-2 block">
            {!single && selectedItems.length > 0 ? `Add ${label}` : label}
          </Label>
        )}
        <div
          className={
            single && selectedItems.length > 0 && selectedItems[0].icon ?
              'relative'
            : 'relative'
          }
        >
          <Input
            placeholder={
              single && selectedItems.length > 0 ?
                selectedItems[0].label
              : searchPlaceholder || placeholder
            }
            value={single && selectedItems.length > 0 ? '' : inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            disabled={isDisabled}
            className={
              single && selectedItems.length > 0 && selectedItems[0].icon ?
                'pl-14'
              : ''
            }
          />
          {single && selectedItems.length > 0 && selectedItems[0].icon && (
            <LazyImage
              src={selectedItems[0].icon}
              alt=""
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-sm pointer-events-none"
              width={20}
              height={20}
            />
          )}
          {single && selectedItems.length > 0 && (
            <button
              onClick={() => onSelectionChange([])}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              disabled={isDisabled}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {isOpen && filteredItems.length > 0 && (
          <>
            <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md overflow-hidden">
              <Virtuoso
                style={{
                  height: Math.min(maxHeight, filteredItems.length * 40 + 8),
                }}
                data={filteredItems}
                itemContent={(index, item) => (
                  <button
                    key={item.id}
                    onClick={() => handleAdd(item)}
                    className="w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-2 transition-colors"
                    disabled={isDisabled}
                  >
                    {item.icon && (
                      <LazyImage
                        src={item.icon}
                        alt=""
                        className="w-5 h-5 rounded-sm"
                        width={20}
                        height={20}
                      />
                    )}
                    <div className="flex flex-col">
                      <span>{item.label}</span>
                      {item.sublabel && (
                        <span className="text-xs text-muted-foreground">
                          {item.sublabel}
                        </span>
                      )}
                    </div>
                  </button>
                )}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
